import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Column, ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Clock,
  Files,
  Loader2,
  Pencil,
  RefreshCw,
  Trash2,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUrlPageState } from "../../utilities/dataTablePagination";
import {
  DocumentCard,
  DocumentCardCoverage,
  DocumentCardStatus,
  deleteDocumentCard,
  generateDocumentCards,
  getDocumentCards,
  patchDocumentCard,
  setDocumentCardsEnabled,
} from "../../DocumentCardsAPI";

// Card admin for one knowledge base: coverage indicator, per-KB enable toggle, generate
// action, a status table, and an editable detail drawer. Mounted as the "Karten" tab in
// RAGManagement. Informal German "du" throughout, matching the portal convention.

interface Props {
  selectedKnowledgeBase: any;
  authentication: any;
  pushError: (message: string) => void;
  showDeleteDialog: (title: string, message: string, onConfirm: () => void) => void;
}

// A pass is "in flight" while any card is still pending or generating; that is the poll
// stop condition — mirrors how FileManagement watches ManagedFileProcessingState.
const isInFlight = (coverage: DocumentCardCoverage | null, cards: DocumentCard[]): boolean =>
  (!!coverage && coverage.pending > 0) ||
  cards.some((c) => c.status === "pending" || c.status === "generating");

const STATUS_META: Record<
  DocumentCardStatus,
  { Icon: LucideIcon; className: string; label: string }
> = {
  generated: { Icon: Check, className: "text-success", label: "Aktuell" },
  stale: { Icon: AlertTriangle, className: "text-warning", label: "Veraltet" },
  pending: { Icon: Clock, className: "text-muted-foreground", label: "Ausstehend" },
  generating: { Icon: RefreshCw, className: "text-primary", label: "Wird erstellt" },
  failed: { Icon: AlertCircle, className: "text-destructive", label: "Fehlgeschlagen" },
};

/** Sortierbarer Spaltenkopf — die `DataTable` rendert selbst keine Sortierköpfe. */
function SortableHeader({
  column,
  children,
}: {
  column: Column<DocumentCard, unknown>;
  children: ReactNode;
}) {
  const sorted = column.getIsSorted();

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp className="h-3.5 w-3.5" aria-hidden />
      ) : sorted === "desc" ? (
        <ArrowDown className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <ArrowUpDown className="h-3.5 w-3.5 opacity-50" aria-hidden />
      )}
    </Button>
  );
}

const StatusCell = ({ card }: { card: DocumentCard }) => {
  const meta = STATUS_META[card.status] ?? STATUS_META.pending;
  const StatusIcon = meta.Icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1.5">
              <StatusIcon className={cn("h-4 w-4", meta.className)} aria-hidden />
              <span>{meta.label}</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>{card.error || meta.label}</TooltipContent>
        </Tooltip>
        {card.editedByUser && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Pencil className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Von Hand bearbeitet – wird nicht automatisch überschrieben
            </TooltipContent>
          </Tooltip>
        )}
      </TooltipProvider>
    </span>
  );
};

const DocumentCardsPanel = ({
  selectedKnowledgeBase,
  authentication,
  pushError,
  showDeleteDialog,
}: Props) => {
  const { t } = useTranslation();
  const kbId: string | undefined = selectedKnowledgeBase?._id;
  const customer: string | undefined = authentication?.customer;
  const enabledSwitchId = useId();
  const synopsisFieldId = useId();
  const topicsFieldId = useId();

  const [enabled, setEnabled] = useState(false);
  const [cards, setCards] = useState<DocumentCard[]>([]);
  const [coverage, setCoverage] = useState<DocumentCardCoverage | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState<DocumentCard | null>(null);
  const [draftSynopsis, setDraftSynopsis] = useState("");
  const [draftTopics, setDraftTopics] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadRequestRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!customer || !kbId) return;
    const requestId = `${customer}-${kbId}`;
    loadRequestRef.current = requestId;
    setLoading(true);
    try {
      const result = await getDocumentCards(customer, kbId);
      if (loadRequestRef.current === requestId) {
        setEnabled(result.enabled);
        setCards(result.cards);
        setCoverage(result.coverage);
      }
    } catch (error) {
      if (loadRequestRef.current === requestId) {
        pushError("Karten konnten nicht geladen werden.");
      }
    } finally {
      if (loadRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [customer, kbId, pushError]);

  // Reload when the selected KB changes.
  useEffect(() => {
    setEnabled(false);
    setCards([]);
    setCoverage(null);
    setEditing(null);
    void load();
  }, [load]);

  // Poll while a generation pass is in flight, then stop. Cleared on unmount / KB change.
  useEffect(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
    if (isInFlight(coverage, cards)) {
      pollTimer.current = setTimeout(() => {
        void load();
      }, 5000);
    }
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [coverage, cards, load]);

  const onToggleEnabled = async (next: boolean) => {
    if (!customer || !kbId) return;
    setBusy(true);
    try {
      await setDocumentCardsEnabled(customer, kbId, next);
      setEnabled(next);
      // Turning it on is the natural moment to kick the first generation pass.
      if (next) {
        try {
          await generateDocumentCards(customer, kbId, {});
        } catch (error) {
          pushError("Die Kartenerzeugung konnte nicht gestartet werden.");
        }
      }
      await load();
    } catch (error) {
      pushError("Die Einstellung konnte nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  };

  const onGenerate = async (force: boolean) => {
    if (!customer || !kbId) return;
    setBusy(true);
    try {
      await generateDocumentCards(customer, kbId, { force });
      await load();
    } catch (error) {
      pushError("Die Kartenerzeugung konnte nicht gestartet werden.");
    } finally {
      setBusy(false);
    }
  };

  const openEditor = (card: DocumentCard) => {
    setEditing(card);
    setDraftSynopsis(card.synopsis || "");
    setDraftTopics((card.topics || []).join(", "));
  };

  const saveEdit = async () => {
    if (!customer || !kbId || !editing) return;
    setSavingEdit(true);
    try {
      const topics = draftTopics
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      await patchDocumentCard(customer, kbId, editing.managedFileId, {
        synopsis: draftSynopsis,
        topics,
      });
      setEditing(null);
      await load();
    } catch (error) {
      pushError("Die Änderung konnte nicht gespeichert werden.");
    } finally {
      setSavingEdit(false);
    }
  };

  const onDelete = (card: DocumentCard) => {
    if (!customer || !kbId) return;
    showDeleteDialog(
      "Karte löschen",
      `Möchtest du die Karte für „${card.title}" wirklich löschen? Sie wird beim nächsten Durchlauf neu erzeugt.`,
      async () => {
        try {
          await deleteDocumentCard(customer, kbId, card.managedFileId);
          await load();
        } catch (error) {
          pushError("Die Karte konnte nicht gelöscht werden.");
        }
      }
    );
  };

  const paginationProps = useUrlPageState("documentCardsPage");

  const columns: ColumnDef<DocumentCard>[] = [
    {
      id: "title",
      accessorFn: (card) => card.title,
      header: ({ column }) => <SortableHeader column={column}>Dokument</SortableHeader>,
      cell: ({ row }) => (
        <span className="block break-words py-1 text-sm">
          <span className="font-medium">{row.original.title}</span>
          {row.original.fileName && row.original.fileName !== row.original.title && (
            <span className="block text-xs text-muted-foreground">{row.original.fileName}</span>
          )}
        </span>
      ),
    },
    {
      id: "status",
      accessorFn: (card) => card.status,
      header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
      size: 160,
      cell: ({ row }) => <StatusCell card={row.original} />,
    },
    {
      id: "topics",
      accessorFn: (card) => (card.topics || []).join(", "),
      header: () => <span className="font-bold text-foreground">Themen</span>,
      enableSorting: false,
      // Wrap so a long topic list breaks onto multiple lines instead of stretching the
      // row past the viewport.
      cell: ({ row }) => (
        <span className="break-words text-sm">
          {(row.original.topics || []).slice(0, 12).join(", ")}
        </span>
      ),
    },
    {
      id: "actions",
      header: () => null,
      enableSorting: false,
      size: 120,
      cell: ({ row }) => (
        <TooltipProvider>
          <span className="inline-flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Karte bearbeiten"
                  onClick={() => openEditor(row.original)}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Karte bearbeiten</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Karte löschen (wird neu erzeugt)"
                  onClick={() => onDelete(row.original)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Karte löschen (wird neu erzeugt)</TooltipContent>
            </Tooltip>
          </span>
        </TooltipProvider>
      ),
    },
  ];

  if (!selectedKnowledgeBase) return null;

  const coverageLabel = coverage
    ? `${coverage.generated}/${coverage.total} Dokumente mit aktueller Karte`
    : "—";

  return (
    <div className="mt-4 max-w-[1400px] space-y-4">
      <p className="text-sm">
        Dokumentkarten beantworten Fragen <em>über</em> die Dokumente dieser Wissensdatenbank —
        welche Dokumente es zu einem Thema gibt, was ein Dokument enthält, Zusammenfassungen.
      </p>

      <div className="flex flex-wrap items-center gap-4">
        <div className={cn("inline-flex items-center gap-2", busy && "opacity-60")}>
          <Switch
            id={enabledSwitchId}
            checked={enabled}
            disabled={busy}
            onCheckedChange={(checked) => void onToggleEnabled(checked)}
          />
          <Label
            htmlFor={enabledSwitchId}
            className={busy ? "cursor-not-allowed" : "cursor-pointer"}
          >
            Dokumentkarten aktiv
          </Label>
        </div>

        <span className="inline-flex items-center gap-2">
          <Files className="h-4 w-4 text-muted-foreground" aria-hidden />
          <span className="text-sm">{coverageLabel}</span>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />}
        </span>

        {coverage && coverage.stale > 0 && (
          <span className="text-xs text-warning">{coverage.stale} veraltet</span>
        )}
        {coverage && coverage.failed > 0 && (
          <span className="text-xs text-destructive">{coverage.failed} fehlgeschlagen</span>
        )}

        <span className="flex-1" />

        <Button
          type="button"
          variant="outline"
          className="gap-2"
          disabled={busy || !enabled}
          onClick={() => void onGenerate(false)}
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          Fehlende/veraltete erzeugen
        </Button>
        <Button
          type="button"
          className="gap-2"
          disabled={busy || !enabled}
          onClick={() => void onGenerate(true)}
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          Alle neu erzeugen
        </Button>
      </div>

      {!enabled && (
        <p className="text-sm text-muted-foreground">
          Für diese Wissensdatenbank sind noch keine Karten aktiviert. Aktiviere sie, um mit der
          Erzeugung zu beginnen.
        </p>
      )}

      <div className="overflow-x-auto">
        <DataTable
          data={cards}
          columns={columns}
          emptyMessage={t("settingsPanel.dataTable.noRecordsText")}
          {...paginationProps}
        />
      </div>

      <Sheet
        open={!!editing}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-[644px]"
        >
          <SheetHeader className="space-y-0 border-b p-4">
            <SheetTitle>{editing ? `Karte: ${editing.title}` : ""}</SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-auto p-4">
            {editing && (
              <div className="flex flex-col gap-4">
                <p className="text-sm text-muted-foreground">
                  Von Hand gespeicherte Karten werden bei der automatischen Erzeugung nicht mehr
                  überschrieben.
                </p>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={synopsisFieldId}>Zusammenfassung</Label>
                  <Textarea
                    id={synopsisFieldId}
                    rows={10}
                    className="resize-none"
                    value={draftSynopsis}
                    onChange={(e) => setDraftSynopsis(e.target.value ?? "")}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={topicsFieldId}>Themen (durch Komma getrennt)</Label>
                  <Input
                    id={topicsFieldId}
                    value={draftTopics}
                    onChange={(e) => setDraftTopics(e.target.value ?? "")}
                  />
                </div>
                {editing.toc && editing.toc.length > 0 && (
                  <div>
                    <div className="mb-1 text-sm font-semibold">Gliederung</div>
                    <ul className="list-disc pl-5 text-sm">
                      {editing.toc.map((entry, i) => (
                        <li key={i} style={{ marginLeft: (entry.level - 1) * 12 }}>
                          {entry.heading}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2 border-t p-4">
            <Button type="button" onClick={() => void saveEdit()} disabled={savingEdit}>
              {savingEdit ? "Speichern…" : "Speichern"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditing(null)}
              disabled={savingEdit}
            >
              Abbrechen
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default DocumentCardsPanel;
